import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Divider, Alert, CircularProgress, useTheme, alpha,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import wineListService from '../../../../services/wineListService';
import type { WineListType, WineListStatus } from '../../../../types/venue';

const WINE_LIST_TYPES: WineListType[] = ['FULL', 'BY_THE_GLASS', 'RESERVE', 'EVENT', 'PRIVATE'];
const WINE_LIST_STATUSES: WineListStatus[] = ['DRAFT', 'ACTIVE', 'ARCHIVED'];

interface FormData {
  name: string;
  type: WineListType | '';
  status: WineListStatus;
  description: string;
}

const INITIAL_FORM: FormData = {
  name: '',
  type: '',
  status: 'DRAFT',
  description: '',
};

const WineListForm: React.FC = () => {
  const navigate = useNavigate();
  const { wineListId } = useParams<{ wineListId: string }>();
  const theme = useTheme();
  const { venueId } = useVenue();
  const isEdit = Boolean(wineListId);
  const backPath = `/admin/venues/${venueId}/wine-lists`;

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (!isEdit || !wineListId) return;
    setLoading(true);
    wineListService.getWineList(venueId, wineListId)
      .then(list => {
        setFormData({
          name: list.name,
          type: list.type,
          status: list.status,
          description: list.description ?? '',
        });
      })
      .catch(() => setApiError('Failed to load wine list.'))
      .finally(() => setLoading(false));
  }, [isEdit, wineListId, venueId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'List name is required.';
    if (!formData.type) newErrors.type = 'Type is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setApiError('');
    try {
      const payload = {
        name: formData.name.trim(),
        type: formData.type as WineListType,
        status: formData.status,
        description: formData.description.trim() || undefined,
      };
      if (isEdit && wineListId) {
        await wineListService.updateWineList(venueId, wineListId, payload);
        navigate(`${backPath}/${wineListId}`);
      } else {
        const created = await wineListService.createWineList(venueId, payload as Parameters<typeof wineListService.createWineList>[1]);
        navigate(`${backPath}/${created._id}`);
      }
    } catch (err) {
      console.error('Failed to save wine list', err);
      setApiError('Failed to save wine list. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Wine Lists
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit Wine List' : 'New Wine List'}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          {/* Name */}
          <TextField
            label="List Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
            fullWidth
            required
            placeholder="e.g. Main Wine List"
          />

          {/* Type + Status */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth required error={Boolean(errors.type)}>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={e => {
                  setFormData(prev => ({ ...prev, type: e.target.value as WineListType }));
                  setErrors(prev => ({ ...prev, type: undefined }));
                }}
              >
                {WINE_LIST_TYPES.map(t => (
                  <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
              {errors.type && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.type}</Typography>
              )}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={e => setFormData(prev => ({ ...prev, status: e.target.value as WineListStatus }))}
              >
                {WINE_LIST_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* Description */}
          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={2}
            placeholder="Optional internal description"
          />

          {/* Actions */}
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Create Wine List'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default WineListForm;
